import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { qk } from './keys';
import type {
  BulkSlotResult,
  CallToken,
  ChatMessage,
  ListSessionsQuery,
  Paginated,
  Session,
  Slot,
  TutorCard,
  SearchTutorsQuery,
} from '../types/api';

// The signed-in user's own sessions — as an admin this is empty, which is
// why these are used from the impersonation workspace.

export function useMySessions(query: ListSessionsQuery, enabled = true) {
  return useQuery({
    queryKey: qk.mySessions(query),
    queryFn: () => api.get<Paginated<Session>>('/sessions', query),
    placeholderData: (prev) => prev,
    enabled,
  });
}

export function useSession(id: string | undefined) {
  return useQuery({
    queryKey: qk.session(id ?? ''),
    queryFn: () => api.get<Session>(`/sessions/${id}`),
    enabled: Boolean(id),
  });
}

export function useCancelSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.post<Session>(`/sessions/${id}/cancel`, { reason }),
    onSuccess: (session) => {
      void qc.invalidateQueries({ queryKey: qk.mySessionsAll });
      void qc.invalidateQueries({ queryKey: qk.session(session.id) });
    },
  });
}

export function useEndSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Session>(`/sessions/${id}/end`),
    onSuccess: (session) => {
      void qc.invalidateQueries({ queryKey: qk.mySessionsAll });
      void qc.invalidateQueries({ queryKey: qk.session(session.id) });
    },
  });
}

/**
 * Fetches the Agora credentials for a call. Participants only — an admin
 * asking for someone else's lesson gets a 403 from the API, which is the
 * point, so this is only ever called while impersonating.
 */
export function useCallToken() {
  return useMutation({
    mutationFn: (id: string) => api.post<CallToken>(`/sessions/${id}/call-token`),
  });
}

export function useBookSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { slotId: string; subject?: string; note?: string }) =>
      api.post<Session>('/sessions', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.mySessionsAll });
      void qc.invalidateQueries({ queryKey: qk.myAvailabilityAll });
    },
  });
}

// ------------------------------------------------------------------ chat --

export function useMessages(sessionId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.messages(sessionId ?? ''),
    queryFn: () => api.get<ChatMessage[]>(`/sessions/${sessionId}/messages`),
    enabled: Boolean(sessionId) && enabled,
  });
}

export function useSendMessage(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api.post<ChatMessage>(`/sessions/${sessionId}/messages`, { body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.messages(sessionId) }),
  });
}

export function useMarkRead(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ marked: number }>(`/sessions/${sessionId}/messages/read`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.messages(sessionId) });
      void qc.invalidateQueries({ queryKey: qk.session(sessionId) });
    },
  });
}

// ---------------------------------------------------- tutors & schedule --

export function useTutors(query: SearchTutorsQuery, enabled = true) {
  return useQuery({
    queryKey: qk.tutors(query),
    queryFn: () => api.get<Paginated<TutorCard>>('/tutors', query),
    placeholderData: (prev) => prev,
    enabled,
  });
}

export function useTutor(id: string | undefined) {
  return useQuery({
    queryKey: qk.tutor(id ?? ''),
    queryFn: () => api.get<TutorCard>(`/tutors/${id}`),
    enabled: Boolean(id),
  });
}

export function useMyAvailability(
  query: { from?: string; to?: string; includeBooked?: boolean },
  enabled: boolean,
) {
  return useQuery({
    queryKey: qk.myAvailability(query),
    queryFn: () => api.get<Slot[]>('/tutors/me/availability', query),
    enabled,
  });
}

export function useTutorAvailability(tutorId: string | undefined) {
  return useQuery({
    queryKey: qk.tutorAvailability(tutorId ?? ''),
    queryFn: () => api.get<Slot[]>(`/tutors/${tutorId}/availability`),
    enabled: Boolean(tutorId),
  });
}

export function useCreateSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { startTime: string; endTime: string }) =>
      api.post<Slot>('/tutors/me/availability', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.myAvailabilityAll }),
  });
}

export function useCreateSlotsBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      slots: { startTime: string; endTime: string }[];
      skipConflicts?: boolean;
    }) => api.post<BulkSlotResult>('/tutors/me/availability/bulk', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.myAvailabilityAll }),
  });
}

export function useDeleteSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slotId: string) =>
      api.delete<{ success: true }>(`/tutors/me/availability/${slotId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.myAvailabilityAll }),
  });
}
